import { getStorage } from "./storage";
import { TOKEN_KEY, REFRESHTOKEN_KEY } from "../ducks/user";
import logger from "./logger";
import { LoginResult } from "../util/DTOs/UserDtos";
import { env } from "../util/env";
import { DocumentDonwloadRequest } from "./DTOs/DocumentationsDtos";
import { IsCordova } from "../util/platformUtils";
import { AttachmentDto } from "./DTOs/AttachmentDtos";

const defaultHeaders = {
    "Content-Type": "application/json; charset=utf-8",
};

export interface IHttpException {
    status: number;
    error: string;
}

function getHeaders(isExternal: boolean = false) {
    if (!isExternal && getStorage().get<string>(TOKEN_KEY)) {
        return {
            ...defaultHeaders,
            Authorization: `Bearer ${getStorage().get<string>(TOKEN_KEY)}`,
        };
    }
    return defaultHeaders;
}

async function handleError(response: Response) {
    const text = await response.text();

    try {
        const json = JSON.parse(text);
        return json;
    } catch (_) {
        return text;
    }
}

async function request(
    input: RequestInfo,
    init?: RequestInit | undefined,
    isExternal: boolean = false
) {
    // Useful when debugging cordova locally to verify
    // the address we fetch from
    if (IsCordova) {
        logger.info(`fetching from ${input}`);
    }

    let response = await fetch(input, init);

    // Try silently refresh tokens without user having to re-login.
    if (!response.ok && response.status === 401 && !isExternal) {
        const refreshToken = getStorage().get<string>(REFRESHTOKEN_KEY);
        if (refreshToken !== undefined) {
            const newTokenResponse = await fetch(
                `${env.APP_API_HOST}/api/user/refreshtoken/${refreshToken}`,
                {
                    method: "GET",
                    headers: {
                        ...getHeaders(),
                    },
                }
            );

            if (newTokenResponse.ok) {
                const result = (await newTokenResponse.json()) as LoginResult;
                getStorage().remove(TOKEN_KEY);
                getStorage().add(TOKEN_KEY, result.accessToken);

                getStorage().remove(REFRESHTOKEN_KEY);
                getStorage().add(REFRESHTOKEN_KEY, result.refreshToken);

                const newInit = {
                    ...init,
                    headers: {
                        ...getHeaders(),
                    },
                } as RequestInit;

                response = await fetch(input, newInit);
            }
        }
    }

    if (!response.ok) {
        if (response.status === 401) {
            try {
                if (cordova) {
                    getStorage().remove(TOKEN_KEY);
                    getStorage().remove(REFRESHTOKEN_KEY);
                    window.location.replace("index.html#/login");
                } else {
                    getStorage().remove(TOKEN_KEY);
                    getStorage().remove(REFRESHTOKEN_KEY);
                    window.location.replace("/login");
                }
            } catch (e) {
                getStorage().remove(TOKEN_KEY);
                getStorage().remove(REFRESHTOKEN_KEY);
                window.location.replace("/login");
            }
        }
        const error = await handleError(response);
        if (typeof error === "object") {
            throw { ...error, status: response.status };
        } else {
            throw { status: response.status, error: error as string };
        }
        // throw response;
    }

    return response;
}

/**
 * Sends a GET request to the target URL
 * @param url The target url
 * @returns Returns a promise containing the data JSON
 */
export async function get<T>(
    url: string,
    isExternal: boolean = false
): Promise<T> {
    const response = await request(url, {
        method: "GET",
        headers: {
            ...getHeaders(isExternal),
        },
    });

    const result = (await response.json()) as T;

    return result;
}

export function portaaliAttachmentDataUrl(id: number): string {
    return `${env.APP_API_HOST}/api/portaali/attachments/${id}`;
}

export function portaaliAttachmentDownloadUrl(id: number): string {
    return `${env.APP_API_HOST}/api/portaali/attachments/${id}/download`;
}

export function documentDownloadUrl(req: DocumentDonwloadRequest): string {
    return `${env.APP_API_HOST}/api/documents/download/${req.type}/${req.objectId}/${req.version}/${req.file}/content`;
}

export async function getPortaaliAttachmentData(
    id: number
): Promise<AttachmentDto> {
    const url = portaaliAttachmentDataUrl(id);
    return await get<AttachmentDto>(url);
}

export async function downloadPortaaliAttachment(id: number): Promise<Blob> {
    const url = `${env.APP_API_HOST}/api/portaali/attachments/${id}/download`;
    return await getBlob(url);
}

export async function getBlob(url: string, mimeType?: string): Promise<Blob> {
    logger.log("Getting file %s", url);

    const response = await request(url, {
        method: "GET",
        headers: {
            ...getHeaders(false),
        },
    });

    return await response.blob();
}

/**
 * Sends a POST request to the target URL
 * @param url The target url
 * @param data The data to post
 * @returns Returns a promise containing the data JSON
 */
export async function post<T>(
    url: string,
    data: object,
    isExternal: boolean = false
): Promise<T> {
    const response = await request(url, {
        method: "POST",
        headers: {
            ...getHeaders(isExternal),
        },
        body: JSON.stringify(data),
    });

    return (await response.json()) as T;
}

/**
 * Sends a PUT request to the target URL
 * @param url The target url
 * @param data The data to post
 * @returns Returns a promise containing the data JSON
 */
export async function put<T>(
    url: string,
    data: object,
    isExternal: boolean = false
): Promise<T> {
    const response = await request(url, {
        method: "PUT",
        headers: {
            ...getHeaders(isExternal),
        },
        body: JSON.stringify(data),
    });

    return (await response.json()) as T;
}

/**
 * Sends a DELETE request to the target URL
 * @param url The target url
 * @returns Returns a promise containing the status in text
 */
export async function deleteResource<T>(
    url: string,
    isExternal: boolean = false
): Promise<string> {
    const response = await request(url, {
        method: "DELETE",
        headers: {
            ...getHeaders(isExternal),
        },
    });

    return response.statusText;
}



// WEBPACK FOOTER //
// ./src/util/request.ts